/**
 * Status / source badges for forms, entries and payments.
 *
 * Labels come from our own status maps (the lookup tables store code-like names such
 * as "PendingApproval"), colours from the Mantine palette so they read well in light
 * and dark themes. Unknown IDs fall back to the API's name / colour.
 */

import type { ReactNode } from 'react';
import {
  IconInbox,
  IconHourglassHigh,
  IconCreditCard,
  IconCircleCheck,
  IconListNumbers,
  IconTicket,
  IconCircleX,
  IconBan,
  IconClockX,
  IconPencil,
  IconBroadcast,
  IconLock,
  IconArchive,
  IconWorld,
  IconUserCircle,
  IconApi,
  IconPlugConnected,
  IconFileImport,
  IconShieldCheck,
} from '@tabler/icons-react';
import { Badge, type ThemedBadgeProps } from '@/components/ui';
import {
  ENTRY_STATUS_META,
  FORM_STATUS_META,
  PAYMENT_STATUS_META,
  ENTRY_SOURCE_META,
  entryStatusLabel,
  formStatusLabel,
  paymentStatusLabel,
  sourceLabel,
} from './formsDisplay';

const ICON = 13;

const ENTRY_ICONS: Record<number, ReactNode> = {
  1: <IconInbox size={ICON} />,
  2: <IconHourglassHigh size={ICON} />,
  3: <IconCreditCard size={ICON} />,
  4: <IconCircleCheck size={ICON} />,
  5: <IconListNumbers size={ICON} />,
  6: <IconTicket size={ICON} />,
  7: <IconCircleX size={ICON} />,
  8: <IconBan size={ICON} />,
  9: <IconClockX size={ICON} />,
};

const FORM_ICONS: Record<number, ReactNode> = {
  1: <IconPencil size={ICON} />,
  2: <IconBroadcast size={ICON} />,
  3: <IconLock size={ICON} />,
  4: <IconArchive size={ICON} />,
};

const SOURCE_ICONS: Record<number, ReactNode> = {
  1: <IconWorld size={ICON} />,
  2: <IconUserCircle size={ICON} />,
  3: <IconApi size={ICON} />,
  4: <IconPlugConnected size={ICON} />,
  5: <IconFileImport size={ICON} />,
  6: <IconShieldCheck size={ICON} />,
};

type BadgeBase = Omit<ThemedBadgeProps, 'children' | 'color'>;

interface StatusBadgeProps extends BadgeBase {
  statusId?: number | null;
  name?: string | null;
  /** API colour (hex) — only used for IDs we do not know. */
  color?: string | null;
  withIcon?: boolean;
}

const baseStyles = { root: { textTransform: 'none' as const, fontWeight: 600, letterSpacing: 0 } };

export function EntryStatusBadge({ statusId, name, color, size = 'md', withIcon = true, ...rest }: StatusBadgeProps) {
  const meta = statusId != null ? ENTRY_STATUS_META[statusId] : undefined;
  return (
    <Badge
      size={size}
      variant="light"
      color={meta?.color || color || 'gray'}
      leftSection={withIcon && statusId != null ? ENTRY_ICONS[statusId] : undefined}
      styles={baseStyles}
      {...rest}
    >
      {entryStatusLabel(statusId, name)}
    </Badge>
  );
}

export function FormStatusBadge({ statusId, name, color, size = 'md', withIcon = true, ...rest }: StatusBadgeProps) {
  const meta = statusId != null ? FORM_STATUS_META[statusId] : undefined;
  return (
    <Badge
      size={size}
      variant="light"
      color={meta?.color || color || 'gray'}
      leftSection={withIcon && statusId != null ? FORM_ICONS[statusId] : undefined}
      styles={baseStyles}
      {...rest}
    >
      {formStatusLabel(statusId, name)}
    </Badge>
  );
}

export function PaymentStatusBadge({ statusId, name, color, size = 'md', withIcon: _withIcon, ...rest }: StatusBadgeProps) {
  const meta = statusId != null ? PAYMENT_STATUS_META[statusId] : undefined;
  return (
    <Badge size={size} variant="dot" color={meta?.color || color || 'gray'} styles={baseStyles} {...rest}>
      {paymentStatusLabel(statusId, name)}
    </Badge>
  );
}

interface SourceBadgeProps extends BadgeBase {
  sourceId?: number | null;
  name?: string | null;
}

export function SourceBadge({ sourceId, name, size = 'sm', ...rest }: SourceBadgeProps) {
  const meta = sourceId != null ? ENTRY_SOURCE_META[sourceId] : undefined;
  return (
    <Badge
      size={size}
      variant="outline"
      color={meta?.color || 'gray'}
      leftSection={sourceId != null ? SOURCE_ICONS[sourceId] : undefined}
      styles={baseStyles}
      {...rest}
    >
      {sourceLabel(sourceId, name)}
    </Badge>
  );
}

export const entryStatusIcon = (id: number) => ENTRY_ICONS[id];
export const sourceIcon = (id: number) => SOURCE_ICONS[id];
export const entryStatusName = (id: number) => entryStatusLabel(id);
export const paymentStatusName = (id: number) => paymentStatusLabel(id);
